import React, { FC, useEffect, useState } from "react";
import {
  Grid,
  InputLabel,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputAdornment,
  SelectChangeEvent,
  Tooltip,
} from "@mui/material";
import { FormFieldProps } from "../../../utils/formFields";
import useStyles from "../model/styles";
import GBFlag from "../../../assets/gb.svg";
import INFlag from "../../../assets/in.svg";
import USAFlag from "../../../assets/countryFlag/usa.svg";
import AustraliaFlag from "../../../assets/countryFlag/aus.svg";
import IrelandFlag from "../../../assets/countryFlag/ireland.svg";
import SpainFlag from "../../../assets/countryFlag/spain.svg";
import DenmarkFlag from "../../../assets/countryFlag/denmark.svg";
import FranceFlag from "../../../assets/countryFlag/france.svg";
import NetherlandsFlag from "../../../assets/countryFlag/netherlands.svg";
import PortugalFlag from "../../../assets/countryFlag/portugal.svg";
import NewZealandFlag from "../../../assets/countryFlag/newzealand.svg";
import CanadaFlag from "../../../assets/countryFlag/canada.svg";
import UAEFlag from "../../../assets/countryFlag/uae.svg";

// Define country codes as a constant for reusability
const COUNTRY_CODES = [
  { value: "+44", label: "+44", icon: GBFlag }, // UK
  { value: "+91", label: "+91", icon: INFlag }, // India
  { value: "+1", label: "+1", icon: USAFlag }, // USA
  { value: "+61", label: "+61", icon: AustraliaFlag }, // Australia
  { value: "+353", label: "+353", icon: IrelandFlag }, // Ireland
  { value: "+34", label: "+34", icon: SpainFlag }, // Spain
  { value: "+45", label: "+45", icon: DenmarkFlag }, // Denmark
  { value: "+33", label: "+33", icon: FranceFlag }, // France
  { value: "+31", label: "+31", icon: NetherlandsFlag }, // Netherlands
  { value: "+351", label: "+351", icon: PortugalFlag }, // Portugal
  { value: "+64", label: "+64", icon: NewZealandFlag }, // New Zealand
  { value: "+1", label: "+1", icon: CanadaFlag }, // Canada
  { value: "+971", label: "+971", icon: UAEFlag }, // UAE
];

const PHONE_ALLOWED_CODES =[
  { value: "+44", label: "+44", icon: GBFlag }, // UK
  { value: "+91", label: "+91", icon: INFlag }, // India
];

const FormContactTextField: FC<FormFieldProps> = (props) => {
  const classes = useStyles();
  const [countryCode, setCountryCode] = useState<string>(
    props?.countryCode ? props?.countryCode : COUNTRY_CODES[0].value,
  ); // Default to the first country code

  const handleCountryCodeChange = (event: SelectChangeEvent<string>) => {
    const selectedCode = event.target.value as string;
    setCountryCode(selectedCode);
    props.onCountryCodeChange?.(selectedCode); // Optional chaining for safety
  };

 useEffect(() => {
  if (props?.countryCode) {
    setCountryCode(props.countryCode);
  } else if (props?.id === "phoneNumber") {
    setCountryCode(PHONE_ALLOWED_CODES[0].value); // default +44
  }
}, [props.countryCode, props.id]);

  const filteredCountryCodes =
  props?.id === "phoneNumber" ? PHONE_ALLOWED_CODES : COUNTRY_CODES;

  const inputRender = (isInGrid: boolean) => {
    return (
      <TextField
        style={{
          marginTop: props?.isNonMarginTop ? "0px" : "",
          overflow: "hidden",
        }}
        hiddenLabel
        variant="outlined"
        {...{
          ...props,
          InputProps: {
            ...props?.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <FormControl
                  disabled={props?.editReadOnly}
                  variant="outlined"
                  sx={{ minWidth: 70, padding: "0px" }}
                >
                  <Select
                    value={countryCode}
                    onChange={handleCountryCodeChange}
                    displayEmpty
                    inputProps={{ "aria-label": "Select Country Code" }}
                    sx={{
                      height: "36px",
                      padding: "0px",
                      marginTop: props?.marginTopCountryCode,
                      marginLeft: props?.marginLeftCountryCode
                        ? props?.marginLeftCountryCode
                        : "-4px",
                      border: "none",
                      "& fieldset": {
                        border: "none",
                        borderRight: "1px solid #CFCFCF",
                        borderRadius: "0px",
                      }, // remove Select outline
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: "200px",
                          width: "70px",
                          border: "none",
                        },
                      },
                    }}
                  >
                    {filteredCountryCodes.map((code) => (
                      <MenuItem key={code.value} value={code.value}>
                        <>
                          <img
                            src={code.icon}
                            alt=""
                            style={{ width: 20, marginRight: 8, marginLeft: 0 }}
                          />{" "}
                          {/* Flag icon */}
                          {code.label}
                        </>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </InputAdornment>
            ),
            sx: {
              background: props?.disableBackground || "#fff",
              ...props?.InputProps?.sx,
              ...(props?.isRounded && { borderRadius: "50px" }),
              ...(isInGrid && {
                "& input:-webkit-autofill": {
                  WebkitBoxShadow: "0 0 0 1000px #fff inset",
                  WebkitTextFillColor: "#000",
                  background: "transparent",
                  height: "0px !important",
                },
                "&.Mui-disabled": {
                  backgroundColor: props?.disableBackground && "#eeecef",
                  color: "black",
                },
              }),
            },
            onBlur: (
              e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => {
              props?.onBlur?.(e);
            },
          },
          inputProps: {
            ...props?.inputProps,
            style: {
              ...props?.inputProps?.style,
            },
          },
        }}
        placeholder={props.placeholder}
        name={props?.id}
        label=""
        margin="normal"
        fullWidth
      />
    );
  };

  return (
    <>
      {props?.label && !props?.inSingleGridForm ? (
        <>
          <Grid
            item
            xs={12}
            sm={props?.md ? 1.5 : 5}
            className={classes.labelBox}
            sx={{ marginTop: props?.isNonMarginTop ? "0px !important" : "" }}
          >
            <InputLabel
              sx={{ color: "#333333", fontWeight: 600, fontSize: "18px" }}
              shrink
              htmlFor={props?.id}
            >
              {props?.label}
              {props?.isRequired && (
                <span style={{ color: "red", fontWeight: 500 }}>*</span>
              )}
            </InputLabel>
          </Grid>
          <Grid
            item
            xs={12}
            sm={props?.md ? props?.md : 7}
            style={{ padding: "0px" }}
          >
            {inputRender(true)}
          </Grid>
        </>
      ) : props?.label && props?.inSingleGridForm ? (
        <>
          <Grid
            item
            xs={12}
            sm={props?.md ? props?.md : 12}
            sx={{
              display: "block",
              alignItems: "center",
              gap: 2,
              ...props?.GridFieldStyle,
            }}
          >
            <Tooltip title={props?.tooltipText || ""} arrow>
              <InputLabel
                sx={{
                  color: "#637381",
                  fontWeight: 600,
                  fontSize: "16px",
                  textAlign: "left !important",
                }}
                shrink
                htmlFor={props?.id}
              >
                {props?.label}
                {props?.isRequired && (
                  <span style={{ color: "red", fontWeight: 500 }}>*</span>
                )}
              </InputLabel>
            </Tooltip>
            {inputRender(true)}
          </Grid>
        </>
      ) : (
        inputRender(false)
      )}
    </>
  );
};

export default FormContactTextField;
